import { useStripe } from "@stripe/react-stripe-js";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { useToast } from "../../components";
import { useGetPartnerOffering } from "../../data/api";
import { buildPathWithParams, useQueryString } from "../../hooks";
import {
  useTrackCheckoutStartedEcommerceEventOnMount,
  useTrackOrderCompletedEcommerceEvent,
  useTrackProductViewedEcommerceEvent,
} from "../../offering/analytics_ecommerce_events";
import {
  Order,
  QuoteInput,
  useLocationQuery,
  useSpaceBookingPolicyQuery,
  useSpaceV2Query,
} from "../../offering/api";
import {
  useMeetingRoomOrderParamsHandlers,
  normalizeMeetingRoomOrderParams,
  toQuoteInput,
  useDateTimePickerHelper,
} from "../../offering/order_params";
import { PartnerConfig } from "../../offering/partner_config";
import { QuoteViewMeetingRoomDesktop } from "../components/quote_view";

import { TemplateOfferingCheckoutBaseV2 } from "./template_offering_checkout_base";
import {
  useAppleGooglePayEmbedInCheckoutPage,
  useEnhanceCheckoutUX,
  useRefactorPaymentHookFeatureFlag,
} from "../../core/splitio";
import { usePrevious } from "../../hooks/use_previous";
import { usePassedDateRedirect } from "../../hooks/use_passed_date_redirect";
import { useQuoteQuery } from "@/offering/hooks/use_quote_query";
import { isHourlyValue } from "@/offering_v2/components/date-time-picker/date_time_picker";
import { usePayment } from "../hooks/use_payment";
import { useCheckoutStripe } from "@/offering/template_offering_checkout_stripe_base";
import { useWeWorkPartnerConfig } from "@/offering/wework/wework_partner_config";
import { offeringConfig } from "@/offering/offering_config";
import {
  getStartDateFromOrderParams,
  getEndDateFromOrderParams,
} from "@/offering/order_params_utils";

interface TemplateOfferingCheckoutStripeMeetingRoomPathParams {
  offeringID: string;
  spaceID: string;
}

interface TemplateOfferingCheckoutStripeMeetingRoomQueryParams {
  locationID?: string;
  startDate?: string;
  endDate?: string;
  fromCatalog?: string;
}

interface TemplateOfferingCheckoutMeetingRoomV2Props {
  partnerConfig: PartnerConfig;
}

export function TemplateOfferingCheckoutMeetingRoomV2(
  props: TemplateOfferingCheckoutMeetingRoomV2Props
) {
  const weworkPartnerConfig = useWeWorkPartnerConfig();
  const partnerConfig =
    props.partnerConfig.slug === offeringConfig.WeWork.partnerSlug
      ? weworkPartnerConfig
      : props.partnerConfig;

  const { firstDayOfWeek, defaultMarketingConsent } = partnerConfig;
  const pageParams =
    useParams<TemplateOfferingCheckoutStripeMeetingRoomPathParams>();
  const partnerOfferingID = pageParams.offeringID;
  const partnerOffering = useGetPartnerOffering(partnerOfferingID);
  const isGoogleApplePayEmbedInCheckoutPage =
    useAppleGooglePayEmbedInCheckoutPage(partnerOffering.partner.id);
  const isEnhanceCheckoutUXFlagEnabled = useEnhanceCheckoutUX();
  const refactorPaymentHookFeatureFlag = useRefactorPaymentHookFeatureFlag();
  const queryParams =
    useQueryString<TemplateOfferingCheckoutStripeMeetingRoomQueryParams>();
  const toast = useToast();
  const {
    orderParams,
    orderParamsError,
    validateOrderParams,
    handleChangeDateRange,
    handleChangePromoCode,
    handleChangeEmail,
    setOrderParams,
    handleChangeAttendees,
  } = useMeetingRoomOrderParamsHandlers({
    ...normalizeMeetingRoomOrderParams(queryParams),
    spaceID: pageParams.spaceID,
  });
  usePassedDateRedirect({
    date: isHourlyValue(orderParams.dateRange)
      ? orderParams.dateRange?.date
      : orderParams.dateRange?.startDate,
    partnerOfferingID,
    redirectURL: `/offerings/${partnerOfferingID}/spaces/${pageParams.spaceID}`,
  });
  const stripe = useStripe();
  const space = useSpaceV2Query(pageParams.spaceID);
  const selectedLocationQuery = useLocationQuery(space?.locationID);
  const { data: location } = selectedLocationQuery;
  const selectedSpaceID = space.id;
  const {
    isOutsideRange,
    isBlockedDate,
    handleNextVisibleDates,
    handleSelectDate,
    timeslots,
    availableDaysError,
    availableTimeSlotsError,
    availableDays,
    disableRangeSelection,
  } = useDateTimePickerHelper(selectedSpaceID, {
    dateRange: {
      startDate: isHourlyValue(orderParams.dateRange)
        ? orderParams.dateRange?.date
        : orderParams.dateRange?.startDate,
      endDate: isHourlyValue(orderParams.dateRange)
        ? orderParams.dateRange?.date
        : orderParams.dateRange?.endDate,
    },
  });

  const [isUpdateQuote, setIsUpdateQuote] = useState<boolean>(true);

  const [spaceLayoutID, setSpaceLayoutID] = useState<string>();

  useEffect(() => {
    if (space) setSpaceLayoutID(space.defaultLayoutID);
  }, [space, setSpaceLayoutID]);

  const capacity = space?.layouts[0]?.capacity;
  const quoteInput = useMemo(
    () =>
      selectedSpaceID && spaceLayoutID
        ? toQuoteInput(
            selectedSpaceID,
            partnerOffering.partner.id,
            orderParams,
            "WL",
            {
              layoutID: spaceLayoutID,
              attendees: orderParams.attendees || capacity,
            },
            undefined,
            availableDays
          )
        : null,
    [
      selectedSpaceID,
      partnerOffering.partner.id,
      orderParams,
      spaceLayoutID,
      capacity,
      availableDays,
    ]
  );
  const [quoteParams, setQuoteParams] = useState<QuoteInput | null>();
  useEffect(() => {
    if (isUpdateQuote && selectedSpaceID) {
      setQuoteParams(quoteInput);
    }
  }, [quoteInput, setQuoteParams, isUpdateQuote, selectedSpaceID]);
  const [order, setOrder] = useState<Order>();
  const {
    data: quote,
    error: quoteError,
    loading: quoteLoading,
  } = useQuoteQuery(quoteParams);

  const trackProductViewed = useTrackProductViewedEcommerceEvent();
  const prevSpace = usePrevious(space);
  useEffect(() => {
    if (quote && space && prevSpace?.id !== space.id) {
      // We want to track item viewed event when space is changed on checkout page
      // so that "Product Viewed" is always greater than "Checkout Started" in funnel
      // and reflect the fact that they are viewing a new item
      trackProductViewed(
        space.name,
        space,
        location,
        quote,
        undefined,
        partnerOffering
      );
    }
  }, [
    space,
    space.name,
    prevSpace,
    quote,
    trackProductViewed,
    location,
    partnerOffering,
  ]);
  useTrackCheckoutStartedEcommerceEventOnMount(
    space.name,
    quote,
    space,
    location,
    undefined,
    partnerOffering
  );
  const trackOrderCompletedEcommerceEvent =
    useTrackOrderCompletedEcommerceEvent(partnerOffering);

  const { data: spaceBookingPolicy } = useSpaceBookingPolicyQuery(
    space.id,
    getStartDateFromOrderParams(orderParams),
    getEndDateFromOrderParams(orderParams)
  );
  const askForBookingReason = !!spaceBookingPolicy?.askForBookingReason;
  const quotePositivePrice = quote ? quote.totalPrice > 0 : false;
  const askForTax =
    (spaceBookingPolicy?.askForTax && quotePositivePrice) || false;
  const requireTaxId = spaceBookingPolicy?.requireTaxId || false;

  const checkoutPaymentProps = {
    stripe,
    requireBillingDetails: quotePositivePrice,
    requirePayment: quotePositivePrice,
    askForTax,
    requireTaxId,
    requireBookingReason: partnerConfig.requireBookingReason,
    initialValues: {
      fullName: "",
      companyName: "",
      phoneNumber: "",
      email: orderParams.email || "",
      billingFullName: "",
      billingPhoneNumber: "",
      billingEmail: "",
      billingStreetAddress: "",
      billingCity: "",
      billingState: "",
      billingPostalCode: "",
      billingCountry: "",
      billingTax: "",
      paymentComplete: false,
      termsChecked: false,
      marketingConsent: defaultMarketingConsent || false,
      bookingReason: "",
      membershipNumber: "",
    },
    useStripePaymentElement: true,
    partnerID: partnerOffering.partner.id,
    quoteID: quote?.id,
    partnerOfferingID: partnerOffering.id,
    legacy__details: {
      layoutID: spaceLayoutID || "",
      attendees: orderParams.attendees || capacity,
    },
    orderParams,
    onComplete: (order: Order) => {
      trackOrderCompletedEcommerceEvent(space.name, order, space, location);

      const pathWithParams = buildPathWithParams(
        `/offerings/confirm_order/${partnerOffering.slug}/${order.id}`
      );
      history.push(pathWithParams);
    },
    onError: (errorMessage: string) => toast.error({ message: errorMessage }),
    onOrderCreated: (createdOrder: Order) => setOrder(createdOrder),
  };

  const paymentForm = usePayment({
    paymentMethod: partnerConfig.payment!,
    ...checkoutPaymentProps,
  });
  const checkoutStripeForm = useCheckoutStripe(checkoutPaymentProps);
  const form = refactorPaymentHookFeatureFlag
    ? paymentForm
    : checkoutStripeForm;

  const history = useHistory();
  const disabledCheckout = !!(
    (!form.values.paymentComplete &&
      !isEnhanceCheckoutUXFlagEnabled &&
      isGoogleApplePayEmbedInCheckoutPage &&
      quotePositivePrice) ||
    quoteLoading ||
    quoteError ||
    orderParamsError.dateRange ||
    orderParamsError.spaceID
  );

  return (
    <TemplateOfferingCheckoutBaseV2
      disabledCheckout={disabledCheckout}
      includeQuantity={false}
      analyticsName={space.name}
      offeringName={space.name}
      partnerOfferingSlug={partnerOfferingID}
      partnerConfig={partnerConfig}
      location={location}
      space={space}
      partnerOffering={partnerOffering}
      askForTax={askForTax}
      requireTaxId={requireTaxId}
      askForBookingReason={askForBookingReason}
      requireBookingReason={partnerConfig.requireBookingReason}
      orderParams={orderParams}
      validateOrderParams={validateOrderParams}
      onChangeEmail={handleChangeEmail}
      quote={quote}
      order={order}
      onPaymentClose={() => setOrder(undefined)}
      onPressBack={() => {
        if (history.length > 1) {
          history.goBack();
        } else {
          const pathWithParams = buildPathWithParams(
            `/offerings/${partnerOfferingID}`
          );
          history.replace(pathWithParams);
        }
      }}
      desktopCheckoutBox={
        <QuoteViewMeetingRoomDesktop
          quote={quote}
          space={space}
          location={location}
          orderParams={orderParams}
          orderParamsErrors={orderParamsError}
          onNextVisibleDates={handleNextVisibleDates}
          onChangeDateRange={handleChangeDateRange}
          onSelectDate={handleSelectDate}
          onChangeAttendees={handleChangeAttendees}
          isOutsideRange={isOutsideRange}
          isBlockedDate={isBlockedDate}
          timeslots={timeslots}
          firstDayOfWeek={firstDayOfWeek}
          availableDaysError={availableDaysError}
          availableTimeSlotsError={availableTimeSlotsError}
          offeringName={space.name}
          partnerID={partnerOffering.partner.id}
          onChangePromoCode={handleChangePromoCode}
          datePickerPlaceholder={partnerConfig.datePickerPlaceholder}
          quoteError={quoteError}
          onResetOrderParams={(orderParams) => setOrderParams(orderParams)}
          onUpdateQuote={(isUpdate) => setIsUpdateQuote(isUpdate)}
          availableDays={availableDays}
          disableRangeSelection={disableRangeSelection}
        />
      }
      mobileCheckoutBox={null}
      form={form}
    />
  );
}
